/**
 * @file Player entity factory.
 *
 * Factories are plain functions that assemble components — deliberately not
 * classes, so "what a player is" stays a composition instead of an inheritance
 * chain (GDD §18).
 */

import {
  Transform,
  Velocity,
  Health,
  Weapon,
  Collider,
  Renderable,
  PlayerControlled,
  StatusEffects,
  Trail
} from '../components/index.js';
import { Layer } from '../config/Layers.js';
import { DEFAULT_WEAPON, WEAPONS } from '../config/Weapons.js';
import { getPlayerTuning } from '../config/PlayerTuning.js';
import { applyPlayerAmmoPolicy } from '../combat/ammoPolicy.js';
import { Prefab } from '../rendering/Prefabs.js';

function resolvePlayerWeaponKey(key) {
  if (key === null) return null;
  return key && WEAPONS[key] ? key : DEFAULT_WEAPON;
}

/**
 * Spawns the player.
 *
 * @param {import('../core/Context.js').GameContext} ctx Shared game context.
 * @param {number} [x] Spawn X.
 * @param {number} [z] Spawn Z.
 * @returns {import('../ecs/Entity.js').Entity}
 */
export function createPlayer(ctx, x = 0, z = 0) {
  const { world, meshFactory } = ctx;
  // The live build, so saved sandbox overrides shape the player at spawn. With
  // no overrides this equals `config.player` field for field.
  const settings = getPlayerTuning();

  const entity = world.createEntity('player');

  entity.add(new Transform(x, 0, z));
  entity.add(new Velocity(settings.speed, settings.acceleration, settings.deceleration));
  // No hit points: the kill countdown is the player's only life (issue #20).
  entity.add(
    Health.countdown(
      settings.killTimerMax,
      settings.killTimerDamagePenalty,
      settings.invulnerabilityTime
    )
  );
  entity.add(new PlayerControlled());
  entity.add(new StatusEffects());
  entity.add(new Trail(settings.trail));

  const weaponKey = resolvePlayerWeaponKey(ctx.playerWeaponKey);
  ctx.playerWeaponKey = weaponKey;
  if (weaponKey) {
    const weapon = applyPlayerAmmoPolicy(
      Weapon.fromArchetype(weaponKey),
      ctx.config?.ammo?.emptyMagazinePolicy
    );
    entity.add(weapon.equip(entity));
  }

  entity.add(
    Collider.fromRadius(
      settings.radius,
      Layer.PLAYER,
      Layer.WALL | Layer.ENEMY | Layer.ENEMY_BULLET | Layer.PICKUP
    )
  );

  entity.add(
    new Renderable(Prefab.PLAYER, meshFactory.acquire(Prefab.PLAYER), {
      yOffset: settings.height * 0.55
    })
  );

  return entity;
}
